import { Component, effect, inject, OnDestroy } from '@angular/core';
import { FormControl, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { JSONContent } from '@tiptap/core';
import { FormValidators } from '@corelabs/angular/forms';
import { ButtonModule } from 'primeng/button';
import { FormInputComponent, FormNumberComponent, FormWysiwygComponent, FormToggleSwitchComponent, ErrorComponent } from '@shared/forms';
import {
    MaterialListComponent,
    MediaVideoComponent,
    MediaAudioComponent,
    MediaImageComponent,
    MediaTextComponent,
    TypeChipComponent,
    UnavailableComponent,
} from '@shared/components';
import { LessonFacade, LessonValue } from '@app/store/lesson';
import { FormOptionsFacade } from '@app/store/form-options';
import { LessonType, MediaType } from '@app/store/shared/models';
import { MediaSelectionService, MediaTarget } from '@app/dialogs';

@Component({
    selector: 'app-lesson-edit-modal',
    templateUrl: './edit.component.html',
    styleUrls: ['./edit.component.css'],
    imports: [
        ReactiveFormsModule,
        TranslateModule,
        ButtonModule,
        FormInputComponent,
        FormNumberComponent,
        FormWysiwygComponent,
        FormToggleSwitchComponent,
        ErrorComponent,
        MediaVideoComponent,
        MediaAudioComponent,
        MediaImageComponent,
        MediaTextComponent,
        TypeChipComponent,
        UnavailableComponent,
        MaterialListComponent,
    ],
})
export class EditComponent implements OnDestroy {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly lessonFacade = inject(LessonFacade);
    private readonly formOptionsFacade = inject(FormOptionsFacade);
    private readonly formBuilder = inject(NonNullableFormBuilder);
    private readonly mediaSelectionService = inject(MediaSelectionService);

    readonly lesson = this.lessonFacade.lesson;
    readonly loading = this.lessonFacade.loading;
    readonly saving = this.lessonFacade.saving;
    readonly error = this.lessonFacade.error;

    readonly form = this.formBuilder.group({
        name: this.formBuilder.control('', [FormValidators.Required, FormValidators.Text]),
        duration: new FormControl<number | null>(null, [Validators.required, Validators.min(0)]),
        // Video/Audio/Text specific
        mediaId: new FormControl<string | null>(null),
        mediaPlaceholderId: new FormControl<string | null>(null),
        transcription: new FormControl<JSONContent | null>(null),
        // Video specific
        completionEnforcement: this.formBuilder.control(false),
        // Materials (PDF)
        materialIds: this.formBuilder.control<string[]>([]),
    });

    private originalMediaId: string | null = null;
    private originalMediaPlaceholderId: string | null = null;
    private mediaChanged = false;

    constructor() {
        const id = this.route.parent?.snapshot.params['id'];
        if (id) {
            this.lessonFacade.getLesson(id);
        }

        // Patch form when entity loads
        effect(() => {
            const lesson = this.lesson();
            if (lesson) {
                this.form.patchValue({
                    name: lesson.name,
                    duration: lesson.duration,
                    materialIds: lesson.materialIds,
                });

                // Patch type-specific fields
                if (lesson.type === 'text') {
                    if (lesson.mediaId) {
                        this.originalMediaId = lesson.mediaId;
                        this.form.controls.mediaId.setValue(lesson.mediaId);
                    }
                } else if (lesson.type === 'video' || lesson.type === 'audio') {
                    this.form.patchValue({
                        transcription: lesson.transcription,
                    });

                    // Patch media
                    if (lesson.mediaId) {
                        this.originalMediaId = lesson.mediaId;
                        this.form.controls.mediaId.setValue(lesson.mediaId);
                    }

                    if (lesson.type === 'video') {
                        this.form.controls.completionEnforcement.setValue(lesson.completionEnforcement);

                        if (lesson.mediaPlaceholderId) {
                            this.originalMediaPlaceholderId = lesson.mediaPlaceholderId;
                            this.form.controls.mediaPlaceholderId.setValue(lesson.mediaPlaceholderId);
                        }
                    }
                }

                this.updateMediaValidators(lesson.type);
            }
        });

        // Close on save success
        effect(() => {
            if (this.lessonFacade.saved()) {
                this.close();
            }
        });

        // Error handling - re-enable form on error
        effect(() => {
            if (this.lessonFacade.saving() === false && this.lessonFacade.saved() === false) {
                this.form.enable();
            }
        });

        // Listen for media selection from dialog
        effect(() => {
            const selection = this.mediaSelectionService.selected();
            if (!selection) return;

            if (selection.target === 'materialIds') {
                const current = this.form.controls.materialIds.value ?? [];
                const id = selection.media.id;
                if (!current.includes(id)) {
                    this.form.controls.materialIds.setValue([...current, id]);
                }
            } else if (selection.target === 'mediaId') {
                this.form.controls.mediaId.setValue(selection.media.id);
                this.mediaChanged = true;
            } else if (selection.target === 'mediaPlaceholderId') {
                this.form.controls.mediaPlaceholderId.setValue(selection.media.id);
            } else {
                return;
            }

            this.mediaSelectionService.clear();
        });
    }

    ngOnDestroy(): void {
        this.lessonFacade.clear();
        this.formOptionsFacade.clear();
    }

    updateDuration(duration: number): void {
        if (this.mediaChanged) {
            this.form.controls.duration.setValue(duration);
            this.mediaChanged = false;
        }
    }

    onSelectMedia(target: MediaTarget, mediaType: MediaType): void {
        this.router.navigate([{ outlets: { dialog: ['select-media', { mediaTarget: target, mediaType }] } }]);
    }

    removeMaterial(id: string): void {
        const current = this.form.controls.materialIds.value ?? [];
        this.form.controls.materialIds.setValue(current.filter((mid) => mid !== id));
    }

    reorderMaterials(ids: string[]): void {
        this.form.controls.materialIds.setValue(ids);
    }

    onSubmit(): void {
        this.form.markAllAsTouched();

        const lesson = this.lesson();
        if (this.form.valid && lesson) {
            this.form.disable();
            const value = this.form.getRawValue();

            let lessonValue: LessonValue;

            if (lesson.type === 'video') {
                lessonValue = {
                    type: 'video',
                    name: value.name,
                    duration: value.duration ?? 0,
                    transcription: value.transcription || undefined,
                    mediaId: value.mediaId ?? null,
                    mediaPlaceholderId: value.mediaPlaceholderId ?? null,
                    originalMediaId: this.originalMediaId,
                    originalMediaPlaceholderId: this.originalMediaPlaceholderId,
                    completionEnforcement: value.completionEnforcement,
                    materialIds: value.materialIds,
                };
            } else if (lesson.type === 'audio') {
                lessonValue = {
                    type: 'audio',
                    name: value.name,
                    duration: value.duration ?? 0,
                    transcription: value.transcription || undefined,
                    mediaId: value.mediaId ?? null,
                    originalMediaId: this.originalMediaId,
                    materialIds: value.materialIds,
                };
            } else {
                lessonValue = {
                    type: 'text',
                    name: value.name,
                    duration: value.duration ?? 0,
                    mediaId: value.mediaId ?? null,
                    originalMediaId: this.originalMediaId,
                    materialIds: value.materialIds,
                };
            }

            this.lessonFacade.updateLesson(lesson.id, lessonValue);
        }
    }

    close(): void {
        this.router.navigate([{ outlets: { modal: null } }]);
    }

    private updateMediaValidators(type: LessonType): void {
        if (type === 'video') {
            this.form.controls.mediaId.setValidators([Validators.required]);
            this.form.controls.mediaPlaceholderId.setValidators([Validators.required]);
        } else if (type === 'audio' || type === 'text') {
            this.form.controls.mediaId.setValidators([Validators.required]);
            this.form.controls.mediaPlaceholderId.clearValidators();
        } else {
            this.form.controls.mediaId.clearValidators();
            this.form.controls.mediaPlaceholderId.clearValidators();
        }

        this.form.controls.mediaId.updateValueAndValidity();
        this.form.controls.mediaPlaceholderId.updateValueAndValidity();
    }
}
